import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../services/ToastContext';
import Navbar from '../components/Navbar';
import '../styles/Friends.css';

function GroupCard({ group, friends, onAddMember, onRemoveMember, onDelete, onMessageGroup }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedFriendId, setSelectedFriendId] = useState('');

  const memberIds = (group.friend_group_members || []).map(m => m.friend_id);
  const members = (group.friend_group_members || []).map(m => m.profiles).filter(Boolean);
  const available = friends.filter(f => !memberIds.includes(f.id));

  const handleAdd = () => {
    if (!selectedFriendId) return;
    onAddMember(group.id, selectedFriendId);
    setSelectedFriendId('');
  };

  return (
    <div className="group-card">
      <div className="group-card-header">
        <button className="group-name-btn" onClick={() => setExpanded(e => !e)}>
          <span className="group-name">{group.name}</span>
          <span className="group-count">{memberIds.length} {memberIds.length === 1 ? 'member' : 'members'}</span>
          <span className="group-chevron">{expanded ? '▲' : '▼'}</span>
        </button>
        <div className="group-header-actions">
          <button className="btn-sm btn-accent" onClick={() => onMessageGroup(group)}>Message</button>
          <button className="group-delete-btn" onClick={() => onDelete(group.id)} title="Delete group">×</button>
        </div>
      </div>

      {expanded && (
        <div className="group-body">
          {members.length === 0 ? (
            <p className="group-empty">No members yet. Add friends below.</p>
          ) : (
            <div className="group-members-list">
              {members.map(m => (
                <div key={m.id} className="group-member-row">
                  <div className="group-member-avatar">
                    {m.avatar_url
                      ? <img src={m.avatar_url} alt={m.full_name} />
                      : m.full_name?.charAt(0).toUpperCase()
                    }
                  </div>
                  <span className="group-member-name">{m.full_name}</span>
                  <button className="group-remove-btn" onClick={() => onRemoveMember(group.id, m.id)} title="Remove">×</button>
                </div>
              ))}
            </div>
          )}

          {available.length > 0 && (
            <div className="add-to-group">
              <select value={selectedFriendId} onChange={e => setSelectedFriendId(e.target.value)}>
                <option value="">Add a friend…</option>
                {available.map(f => (
                  <option key={f.id} value={f.id}>{f.full_name}</option>
                ))}
              </select>
              <button className="btn-sm btn-primary-sm" onClick={handleAdd} disabled={!selectedFriendId}>Add</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Friends() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      const [friendsRes, requestsRes, groupsRes] = await Promise.all([
        api.get('/friends/'),
        api.get('/friends/requests'),
        api.get('/friends/groups')
      ]);
      setFriends(friendsRes.data.friends);
      setRequests(requestsRes.data.requests);
      setGroups(groupsRes.data.groups);
    } catch {
      addToast('Failed to load friends data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();
    setFormError('');
    const value = searchValue.trim();
    if (!value) return;
    const isPhone = /^[+\d\s()[\]-]{7,}$/.test(value) && !/[@.]/.test(value);
    const payload = isPhone ? { friend_phone: value } : { friend_email: value };
    try {
      await api.post('/friends/request', payload);
      setSearchValue('');
      addToast('Friend request sent!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to send friend request');
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await api.post(`/friends/request/${requestId}/accept`);
      fetchData();
      addToast('Friend request accepted!');
    } catch {
      addToast('Failed to accept request', 'error');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await api.post(`/friends/request/${requestId}/reject`);
      setRequests(prev => prev.filter(r => r.id !== requestId));
    } catch {
      addToast('Failed to decline request', 'error');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      const res = await api.post('/friends/groups', { name: newGroupName.trim() });
      setGroups(prev => [...prev, res.data.group]);
      setNewGroupName('');
      setShowCreateGroup(false);
      addToast(`Group "${res.data.group.name}" created`);
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to create group', 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    const group = groups.find(g => g.id === groupId);
    try {
      await api.delete(`/friends/groups/${groupId}`);
      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (group) addToast(`"${group.name}" deleted`, 'info');
    } catch {
      addToast('Failed to delete group', 'error');
    }
  };

  const handleAddMember = async (groupId, friendId) => {
    try {
      await api.post(`/friends/groups/${groupId}/members`, { friend_id: friendId });
      const res = await api.get('/friends/groups');
      setGroups(res.data.groups);
      addToast('Friend added to group');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to add member', 'error');
    }
  };

  const handleRemoveMember = async (groupId, friendId) => {
    try {
      await api.delete(`/friends/groups/${groupId}/members/${friendId}`);
      setGroups(prev => prev.map(g =>
        g.id === groupId
          ? { ...g, friend_group_members: g.friend_group_members.filter(m => m.friend_id !== friendId) }
          : g
      ));
    } catch {
      addToast('Failed to remove member', 'error');
    }
  };

  const handleMessageGroup = async (group) => {
    const memberIds = (group.friend_group_members || []).map(m => m.friend_id);
    if (memberIds.length === 0) {
      addToast('Add friends to the group before messaging', 'error');
      return;
    }
    try {
      const res = await api.post('/messages/conversations', {
        member_ids: memberIds,
        name: group.name,
        is_group: true
      });
      navigate(`/messages/${res.data.conversation.id}`);
    } catch {
      addToast('Failed to create group message', 'error');
    }
  };

  return (
    <div className="page-container">
      <Navbar />

      <div className="friends-container">
        <div className="friends-content">

          <section className="add-friend-section">
            <h2>Add Friend</h2>
            <form onSubmit={handleSendRequest}>
              <div className="form-group">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Email address or phone number"
                  required
                />
                <button type="submit" className="btn-primary">Send Request</button>
              </div>
            </form>
            {formError && <div className="error-message">{formError}</div>}
          </section>

          {requests.length > 0 && (
            <section className="friend-requests-section">
              <h2>Friend Requests ({requests.length})</h2>
              <div className="requests-list">
                {requests.map(req => (
                  <div key={req.id} className="request-card">
                    <div className="request-info">
                      <h3>{req.profiles.full_name}</h3>
                      <p>{req.profiles.email}</p>
                    </div>
                    <div className="request-actions">
                      <button onClick={() => handleAcceptRequest(req.id)} className="btn-primary">Accept</button>
                      <button onClick={() => handleRejectRequest(req.id)} className="btn-secondary">Decline</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="friends-list-section">
            <h2>Your Friends ({friends.length})</h2>
            {loading ? (
              <div className="loading">Loading…</div>
            ) : friends.length === 0 ? (
              <div className="empty-state">
                <p>No friends yet. Enter a friend's email or phone number above to send them a request.</p>
              </div>
            ) : (
              <div className="friends-grid">
                {friends.map(friend => (
                  <div key={friend.id} className="friend-card">
                    <div className="friend-avatar">
                      {friend.avatar_url
                        ? <img src={friend.avatar_url} alt={friend.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : friend.full_name?.charAt(0).toUpperCase()
                      }
                    </div>
                    <h3>{friend.full_name}</h3>
                    <p>{friend.email}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="friend-groups-section">
            <div className="section-header-row">
              <h2>Your Groups ({groups.length})</h2>
              <button className="btn-sm btn-accent" onClick={() => setShowCreateGroup(g => !g)}>
                {showCreateGroup ? 'Cancel' : '+ New Group'}
              </button>
            </div>

            {showCreateGroup && (
              <form onSubmit={handleCreateGroup} className="create-group-form">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  placeholder="Group name (e.g. College Friends)"
                  autoFocus
                  required
                />
                <button type="submit" className="btn-primary">Create</button>
              </form>
            )}

            {groups.length === 0 && !showCreateGroup ? (
              <p className="group-empty-state">Create groups to organize your friends and message them all at once.</p>
            ) : (
              <div className="groups-list">
                {groups.map(group => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    friends={friends}
                    onAddMember={handleAddMember}
                    onRemoveMember={handleRemoveMember}
                    onDelete={handleDeleteGroup}
                    onMessageGroup={handleMessageGroup}
                  />
                ))}
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}

export default Friends;
